import React, {useMemo, useRef, useState} from 'react'
import moment from 'moment'
import PageLayout from '../../../components/layout/PageLayout'
import Text from '../../../components/text/Text'
import WizButton from '../../../components/button/WizButton'
import WizImage from '../../../components/image/WizImage'
import WizSelect from '../../../components/select/WizSelect'
import WizSelectButton from '../../../components/button/WizSelectButton'
import UnderLineInput from '../../../components/input/UnderLineInput'
import DateInput from '../../../components/input/DateInput'
import WizModal from '../../../components/modal/WizModal'
import TableLayout from '../../../components/table/TableLayout'
import TableItem from '../../../components/table/TableItem'
import TableHeader from '../../../components/table/TableHeader'
import useQueryUserBadges, {IUserBadgeRow} from '../../../hooks/api/badge/useQueryUserBadges'
import {ADMIN_USER_BADGE_DEFAULT_LIMIT, UserBadgeStatus} from '../../../apis/badge/getAdminUserBadges'
import {grantBadge} from '../../../apis/badge/grantBadge'
import {revokeBadge} from '../../../apis/badge/revokeBadge'
import {editUserBadge} from '../../../apis/badge/editUserBadge'

interface IProps {
    className?: string
}

const PAGE_SIZE = ADMIN_USER_BADGE_DEFAULT_LIMIT

const STATUS_OPTIONS: {id: '' | UserBadgeStatus; value: string}[] = [
    {id: '', value: 'All'},
    {id: 'active', value: 'Active'},
    {id: 'expired', value: 'Expired'},
    {id: 'revoked', value: 'Revoked'},
]

const fmtDate = (unixSec?: number) => {
    if (!unixSec) return '-'
    return moment.unix(unixSec).format('YYYY-MM-DD HH:mm')
}

// datetime-local 입력값 <-> unix sec 변환
const toDatetimeLocal = (unixSec?: number) => (unixSec ? moment.unix(unixSec).format('YYYY-MM-DDTHH:mm') : '')

const statusStyle = (status: UserBadgeStatus) => {
    switch (status) {
        case 'active':
            return 'bg-green-100 text-green-700'
        case 'expired':
            return 'bg-gray-100 text-gray-600'
        case 'revoked':
            return 'bg-red-100 text-red-700'
        default:
            return 'bg-gray-100 text-gray-600'
    }
}

const UserBadgePage: React.FC<IProps> = ({className}) => {
    // 적용된 필터(applied)와 입력값(input)을 분리 — User ID는 Apply를 눌러야 반영.
    const [userIdInput, setUserIdInput] = useState('')
    const [userId, setUserId] = useState('')
    const [badgeId, setBadgeId] = useState('')
    const [status, setStatus] = useState<'' | UserBadgeStatus>('')
    const [skip, setSkip] = useState(0)

    // 날짜 직접 수정 모달 (PATCH /admin/user-badges/:id)
    const editModalRef = useRef<any>()
    const [editTarget, setEditTarget] = useState<IUserBadgeRow | null>(null)
    const [editGrantedAt, setEditGrantedAt] = useState('')
    const [editExpiredAt, setEditExpiredAt] = useState('')

    const filter = useMemo(
        () => ({
            user_id: userId || undefined,
            badge_id: badgeId || undefined,
            status: status || undefined,
            limit: PAGE_SIZE,
            skip,
        }),
        [userId, badgeId, status, skip],
    )

    const {rows, catalog, refetch, isFetching} = useQueryUserBadges(filter)

    // 배지 필터 드롭다운 — 카탈로그에서 구성. 코드 포함이라 라벨 유일.
    const badgeOptions = useMemo(
        () => [
            {id: '', value: 'All Badges'},
            ...catalog
                .filter(t => t.id)
                .map(t => ({id: t.id as string, value: `${t.name_i18n?.en || t.code} (${t.code})`})),
        ],
        [catalog],
    )

    const lang = localStorage?.getItem('lang') === 'ko' ? 'ko' : 'en'

    // total 미제공 → 반환 개수가 limit과 같으면 다음 페이지가 있다고 추정.
    const hasNext = rows.length === PAGE_SIZE
    const hasPrev = skip > 0
    const pageIndex = Math.floor(skip / PAGE_SIZE) + 1

    const onApplyUserId = () => {
        setUserId(userIdInput.trim())
        setSkip(0)
    }

    const onResetFilters = () => {
        setUserIdInput('')
        setUserId('')
        setBadgeId('')
        setStatus('')
        setSkip(0)
    }

    const onBadgeChange = (val: string) => {
        const opt = badgeOptions.find(o => o.value === val)
        setBadgeId(opt?.id || '')
        setSkip(0)
    }

    const onRevoke = async (targetUserId: string, badge_id: string, badgeName: string) => {
        if (!window.confirm(`Revoke "${badgeName}" from user ${targetUserId}?`)) return
        const {data: res, error} = await revokeBadge(badge_id, [targetUserId])
        if (res) {
            alert(`Revoked (${res.revoked ?? 1})`)
            refetch()
        } else {
            alert(`Revoke failed: ${error || 'Unknown error'}`)
        }
    }

    const onRegrant = async (targetUserId: string, badge_id: string, badgeName: string, type: string) => {
        const notice =
            type === 'limited'
                ? '\n\n⚠️ 기간제(limited) 배지는 만료일이 "당월 말일"로 재설정됩니다. 임의 날짜 지정은 불가합니다.'
                : ''
        if (!window.confirm(`Re-grant "${badgeName}" to user ${targetUserId}?${notice}`)) return
        const {data: res, error} = await grantBadge(badge_id, [targetUserId])
        if (res) {
            alert(`Granted (${res.granted ?? 1})`)
            refetch()
        } else {
            alert(`Grant failed: ${error || 'Unknown error'}`)
        }
    }

    const onEditOpen = (row: IUserBadgeRow) => {
        setEditTarget(row)
        setEditGrantedAt(toDatetimeLocal(row.granted_at))
        setEditExpiredAt(toDatetimeLocal(row.expired_at))
        editModalRef.current?.show()
    }

    const onEditSave = async () => {
        if (!editTarget) return
        const body: {granted_at?: number; expired_at?: number} = {}
        if (editGrantedAt) body.granted_at = moment(editGrantedAt).unix()
        if (editExpiredAt) body.expired_at = moment(editExpiredAt).unix()
        if (body.granted_at === undefined && body.expired_at === undefined) {
            alert('획득일 또는 만료일 중 하나 이상을 입력하세요.')
            return
        }
        const {data: res, error} = await editUserBadge(editTarget.id, body)
        if (res) {
            alert('Updated')
            editModalRef.current?.close()
            setEditTarget(null)
            refetch()
        } else {
            alert(`Update failed: ${error || 'Unknown error'}`)
        }
    }

    return (
        <PageLayout title={'User Badge Management'}>
            {/* 주의사항 — API 제약으로 인한 동작 한계 명시 */}
            <div className={'bg-yellow-50 border border-yellow-300 rounded-[10px] p-[20px] space-y-[6px]'}>
                <Text className={'text-ti2 text-yellow-800'}>⚠️ 주의사항 (API 제약)</Text>
                <ul className={'list-disc pl-[20px] space-y-[4px]'}>
                    <li>
                        <Text className={'text-body3 text-font02'}>
                            어드민 전체조회(GET /admin/user-badges)를 사용합니다. status 필터로{' '}
                            <b>active / expired / revoked</b> 모두 조회 가능합니다.
                        </Text>
                    </li>
                    <li>
                        <Text className={'text-body3 text-font02'}>
                            <b>Edit</b> 버튼으로 획득일/만료일을 직접 수정할 수 있습니다(PATCH /admin/user-badges/:id).
                            임의 날짜 지정 가능 — 회수 후 재발급이 더 이상 필요하지 않습니다.
                        </Text>
                    </li>
                    <li>
                        <Text className={'text-body3 text-font02'}>
                            grant(재발급) 시 기간제(limited) 배지의 만료일은 <b>당월 말일로 자동 설정</b>되지만, 이후
                            Edit로 원하는 날짜로 조정할 수 있습니다.
                        </Text>
                    </li>
                    <li>
                        <Text className={'text-body3 text-font02'}>
                            서버가 총 개수를 주지 않아 페이지네이션은 limit({PAGE_SIZE}) 기준 다음/이전 이동만
                            가능합니다. 이미지·이름은 배지 카탈로그와 조인해 표시합니다.
                        </Text>
                    </li>
                </ul>
            </div>

            {/* 필터 */}
            <div className={'mt-[25px] flex items-end gap-[15px] flex-wrap'}>
                <div className={'w-[280px] max-w-full'}>
                    <Text className={'text-body3 text-font02 mb-[5px]'}>User ID</Text>
                    <UnderLineInput
                        value={userIdInput}
                        placeholder={'Filter by user ID (optional)'}
                        onChanged={text => setUserIdInput(text || '')}
                    />
                </div>
                <WizButton text={'Apply'} type={'primary'} onClick={onApplyUserId} />
                <div>
                    <Text className={'text-body3 text-font02 mb-[5px]'}>Badge</Text>
                    <WizSelect
                        customOption={badgeOptions}
                        value={badgeOptions.find(o => o.id === badgeId)?.value || 'All Badges'}
                        onChange={onBadgeChange}
                    />
                </div>
                <div>
                    <Text className={'text-body3 text-font02 mb-[5px]'}>Status</Text>
                    <WizSelectButton
                        data={STATUS_OPTIONS}
                        onChange={e => {
                            setStatus(e.id)
                            setSkip(0)
                        }}
                        activeValue={STATUS_OPTIONS.find(o => o.id === status)}
                    />
                </div>
                <WizButton text={'Reset'} type={'border'} onClick={onResetFilters} />
            </div>

            {/* 결과 */}
            <div className={'mt-[30px]'}>
                <div className={'flex items-center justify-between mb-[10px]'}>
                    <Text className={'text-body3 text-font03'}>
                        Page {pageIndex} · {rows.length} row(s) {isFetching ? '· loading…' : ''}
                    </Text>
                    <div className={'flex items-center gap-[8px]'}>
                        <WizButton
                            text={'Prev'}
                            type={'border'}
                            disabled={!hasPrev}
                            onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
                        />
                        <WizButton
                            text={'Next'}
                            type={'border'}
                            disabled={!hasNext}
                            onClick={() => setSkip(skip + PAGE_SIZE)}
                        />
                    </div>
                </div>

                <TableHeader className={'grid-cols-9 gap-x-[10px] md:hidden'}>
                    <Text>Image</Text>
                    <Text>User ID</Text>
                    <Text>Name</Text>
                    <Text>Type</Text>
                    <Text>Category</Text>
                    <Text>획득일</Text>
                    <Text>만료일</Text>
                    <Text className={'text-center'}>Status</Text>
                    <Text className={'text-center'}>Actions</Text>
                </TableHeader>
                <TableLayout>
                    {rows.map(badge => {
                        const displayName = badge.name_i18n?.[lang] || badge.badge_name || badge.badge_code
                        return (
                            <TableItem
                                key={badge.id}
                                className={
                                    'grid-cols-9 gap-x-[10px] md:block md:space-y-[10px] sm:px-[15px] items-center'
                                }>
                                <div className={'flex items-center'}>
                                    {badge.image_mini ? (
                                        <WizImage
                                            src={`${badge.image_mini}?type=webp&width=200&quality=80`}
                                            className={'w-[40px] h-[40px] rounded-full object-cover'}
                                        />
                                    ) : (
                                        <div
                                            className={
                                                'w-[40px] h-[40px] rounded-full bg-line02 flex items-center justify-center'
                                            }>
                                            <Text className={'text-body3 text-font03'}>N/A</Text>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <Text className={'text-body3 text-font03 md:hidden'}>User ID</Text>
                                    <Text className={'text-ti2 break-all'}>{badge.user_id}</Text>
                                </div>
                                <div>
                                    <Text className={'text-body3 text-font03 md:hidden'}>Name</Text>
                                    <Text className={'text-ti2'}>{displayName}</Text>
                                    <Text className={'text-caption text-font03'}>{badge.badge_code}</Text>
                                </div>
                                <div>
                                    <Text className={'text-body3 text-font03 md:hidden'}>Type</Text>
                                    <Text className={'text-ti2'}>{badge.badge_type}</Text>
                                </div>
                                <div>
                                    <Text className={'text-body3 text-font03 md:hidden'}>Category</Text>
                                    <Text className={'text-ti2'}>{badge.category}</Text>
                                </div>
                                <div>
                                    <Text className={'text-body3 text-font03 md:hidden'}>획득일</Text>
                                    <Text className={'text-ti2'}>{fmtDate(badge.granted_at)}</Text>
                                </div>
                                <div>
                                    <Text className={'text-body3 text-font03 md:hidden'}>만료일</Text>
                                    <Text className={'text-ti2'}>
                                        {badge.badge_type === 'permanent' ? '영구' : fmtDate(badge.expired_at)}
                                    </Text>
                                </div>
                                <div className={'text-center'}>
                                    <span
                                        className={`inline-block px-[10px] py-[3px] rounded-[5px] text-body3 ${statusStyle(
                                            badge.status,
                                        )}`}>
                                        {badge.status}
                                    </span>
                                </div>
                                <div className={'flex justify-center gap-x-[6px] gap-y-[6px] flex-wrap'}>
                                    {badge.status !== 'revoked' && (
                                        <WizButton
                                            text={'Edit'}
                                            type={'border'}
                                            onClick={() => onEditOpen(badge)}
                                        />
                                    )}
                                    <WizButton
                                        text={'Re-grant'}
                                        type={'primary'}
                                        onClick={() =>
                                            onRegrant(badge.user_id, badge.badge_id, displayName, badge.badge_type)
                                        }
                                    />
                                    {badge.status !== 'revoked' && (
                                        <WizButton
                                            text={'Revoke'}
                                            type={'red'}
                                            onClick={() => onRevoke(badge.user_id, badge.badge_id, displayName)}
                                        />
                                    )}
                                </div>
                            </TableItem>
                        )
                    })}
                </TableLayout>

                {rows.length === 0 && (
                    <div className={'py-[40px] text-center'}>
                        <Text className={'text-body2 text-font03'}>조건에 맞는 유저 배지가 없습니다.</Text>
                    </div>
                )}
            </div>

            <div className={'h-[40px]'} />

            {/* 획득일/만료일 직접 수정 모달 */}
            <WizModal ref={editModalRef} title={'Edit User Badge'} onClose={() => setEditTarget(null)}>
                <div className={'space-y-[15px]'}>
                    <div>
                        <Text className={'text-body3 text-font03'}>User</Text>
                        <Text className={'text-ti2 break-all'}>{editTarget?.user_id}</Text>
                    </div>
                    <div>
                        <Text className={'text-body3 text-font03'}>Badge</Text>
                        <Text className={'text-ti2'}>
                            {editTarget?.name_i18n?.[lang] || editTarget?.badge_name || editTarget?.badge_code}
                        </Text>
                    </div>
                    <div>
                        <Text className={'text-body3 text-font02'}>획득일 (granted_at)</Text>
                        <DateInput value={editGrantedAt} onChange={e => setEditGrantedAt(e.target.value)} />
                    </div>
                    <div>
                        <Text className={'text-body3 text-font02'}>만료일 (expired_at)</Text>
                        <DateInput value={editExpiredAt} onChange={e => setEditExpiredAt(e.target.value)} />
                        <Text className={'text-caption text-font03 mt-[4px]'}>
                            임의 날짜 지정 가능. 비워두면 해당 필드는 변경하지 않습니다(만료 해제는 불가).
                        </Text>
                    </div>
                    <div className={'flex justify-end gap-[8px] pt-[5px]'}>
                        <WizButton text={'Cancel'} type={'border'} onClick={() => editModalRef.current?.close()} />
                        <WizButton text={'Save'} type={'primary'} onClick={onEditSave} />
                    </div>
                </div>
            </WizModal>
        </PageLayout>
    )
}

export default UserBadgePage
